/**
 * Guardas do Formulario de Entrada 2.0 (secao 7 de docs/onboarding-spec.md).
 *
 * Sao duas direcoes complementares, decididas pelo MESMO predicado (`needsOnboarding`),
 * o que torna um ciclo de redirecionamento impossivel:
 *
 * - Guarda 1 — `DashboardLayout`: `needsOnboarding(user) === true` sai de `/app/*` para
 *   `/onboarding`.
 * - Guarda 2 — `OnboardingGate` (elemento da rota `/onboarding`):
 *   `needsOnboarding(user) === false` sai de `/onboarding` para a home do papel.
 *
 * Como as duas condicoes sao negacao uma da outra, so uma delas pode ser verdadeira para
 * um mesmo usuario — nunca as duas ao mesmo tempo, que e o que produziria o ping-pong.
 *
 * A rota `/onboarding` fica FORA do `DashboardLayout` (a pagina tem casco proprio), entao
 * a checagem de autenticacao precisa acontecer aqui — este e o unico ponto por onde ela passa.
 */
import { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { LoadingOverlay } from '@/components/ui'
import { useAuthStore } from '@/stores/authStore'
import { useThemeInit } from '@/stores/uiStore'
import { homePathForRole, needsOnboarding } from './onboardingAccess'
import { NO_PLAN_PATH, needsPlan } from '@/lib/planFeatures'


export function OnboardingGate() {
  const { user, isAuthenticated, isLoading, checkAuth } = useAuthStore()

  useThemeInit()

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // A decisao "ja concluiu, nao entra" e tomada UMA vez, com o usuario do primeiro render
  // em que ele e conhecido. Motivo: ao concluir, `POST /onboarding/complete` faz
  // `setUser({ onboarding_completed: true })`, e um redirect reativo tiraria a pagina do ar
  // antes de o aluno ver a tela de confirmacao. Sair dali e um clique, com `replace: true`.
  // Estado, nao ref: ler/escrever ref durante o render e proibido (react-hooks/refs).
  // O `setState` no corpo do render e o padrao que a propria doc do React indica para
  // "ajustar estado quando algo muda" — ele reinicia o render antes de pintar, sem o
  // efeito-cascata que um useEffect teria.
  const [allowed, setAllowed] = useState<boolean | null>(null)
  if (allowed === null && !isLoading && user) {
    setAllowed(needsOnboarding(user))
  }

  if (isLoading && !user) {
    return <LoadingOverlay message="Carregando..." />
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  // r7-25: sem plano vinculado nao ha formulario — a tela "sem plano" vem antes.
  if (needsPlan(user)) {
    return <Navigate to={NO_PLAN_PATH} replace />
  }

  // Enquanto o `/auth/me` nao confirmou o usuario pela primeira vez, nada e decidido — o
  // `user` do localStorage pode estar velho. Depois de decidido, o recheck acontece em
  // segundo plano: voltar ao overlay desmontaria a pagina (e o que o aluno ja digitou).
  if (allowed === null) {
    return <LoadingOverlay message="Carregando..." />
  }

  if (!allowed) {
    return <Navigate to={homePathForRole(user)} replace />
  }

  return <Outlet />
}
